// ---------------------------------------------------------------------------
// FEMA IPAWS — the frontend's wire types, annotated field by field with the
// RULE and its SOURCE (2026-08-24), the way the backend models are:
//   Fusion-Backend src/features/fema/types/cap12.types.ts        (base CAP 1.2)
//   Fusion-Backend src/features/fema/types/ipawsProfile.types.ts (IPAWS profile + RescAlert policy)
// Those two are the zod models the backend validates against; THIS file is the
// TypeScript shape the form builds and the API answers. When they disagree,
// the backend wins — a payload that passes here can still come back as 400
// findings.
//
// SOURCE LEGEND (the quotes below are copied from the local 1:1 spec copies):
//   CAP §3.2.1 / §3.2.2 / §3.2.3 / §3.2.4 — OASIS CAP v1.2 data dictionary
//     (<alert> / <info> / <resource> / <area>).
//     https://docs.oasis-open.org/emergency/cap/v1.2/CAP-v1.2.html
//     local copy: Fusion-Backend Docs/issues/planning/fema-ipaws-aosp/CAP-1.2-DATA-DICTIONARY.md
//   Profile Table 1 / Table 2 — OASIS CAP v1.2 IPAWS Profile v1.0 CS01.
//     https://docs.oasis-open.org/emergency/cap/v1.2/ipaws-profile/v1.0/cs01/cap-v1.2-ipaws-profile-cs01.html
//     local copy: Fusion-Backend Docs/issues/planning/fema-ipaws-aosp/IPAWS-PROFILE-CONSTRAINTS.md
//   FEMA §4.x — RescAlert IPAWS AOSP Implementation Requirements v1.2.1
//     (Wesley Cassaday), digested in Fusion-Backend Docs/.../FEMA-REQUIREMENTS-DIGEST.md
//   FEMA glossary — "Event Code Descriptions for Use with IPAWS" (FEMA, February 2025).
//     local copy: Fusion-Backend Docs/issues/planning/fema-ipaws-aosp/FEMA-IPAWS-EVENT-CODE-GLOSSARY-2025-02.md
//   FCC §11.31(e) — 47 CFR 11.31(e), the authorized SAME event code table.
//     local copy: Fusion-Backend Docs/issues/planning/fema-ipaws-aosp/FCC-47CFR-11.31-EVENT-CODES.md
//   RescAlert — our own decision, with who/when and the card that owns it.
//   Backend — the contract as the backend code implements it (file named).
// ---------------------------------------------------------------------------

import {
  FEMA_CREDENTIAL_STATES,
  FEMA_ENVIRONMENT_CLASSES,
  FEMA_EXPIRY_BANDS,
  type FemaCredentialState,
  type FemaEnvironmentClass,
  type FemaExpiryBand,
} from 'types/femaTenant.types';

/**
 * RescAlert: the FEMA environment CLASSES, upper case, as a service process names its own class
 * (`IPAWS_ENVIRONMENT`, ExtServices/IpawsService, utils/environment.utils.ts). TDL = FEMA's Test
 * Development Lab. The legacy `FemaMeta.environment` and the result panel use this form.
 */
export const VALID_FEMA_ENVIRONMENTS = ['TDL', 'DEMO', 'PRODUCTION'] as const;
export type FemaEnvironment = (typeof VALID_FEMA_ENVIRONMENTS)[number];

/**
 * Multi-COG contract §0 and §1 (2026-09-25): the class tokens ON THE WIRE, lower case (the DB
 * enum `Ipaws_Tenant.environment`; Backend `ZIpawsEnvironment`,
 * ExtServices/IpawsService/src/types/ipaws.types.ts:22). A workplace holds one COG per class. The
 * Alert Creator sends the chosen class as the multipart field `environment` beside `data` on
 * `POST /fusion/v2/fema`, and as `?environment=` on `GET /fusion/v2/fema/jurisdictions`. Update
 * and Cancel send no class.
 *
 * The one list lives in `types/femaTenant.types.ts` (`FEMA_ENVIRONMENT_CLASSES`). This name is an
 * alias of that array. Do not declare the values here again.
 */
export const VALID_FEMA_ENVIRONMENT_CLASSES = FEMA_ENVIRONMENT_CLASSES;
export type { FemaEnvironmentClass };

/** Type guard for a class token from the server or from the draft. */
export const isFemaEnvironmentClass = (value: unknown): value is FemaEnvironmentClass =>
  VALID_FEMA_ENVIRONMENT_CLASSES.some((environmentClass) => environmentClass === value);

/**
 * Multi-COG contract §2 and §3: the certificate expiry band. `ok` = more than 90 days. This name
 * is an alias of `FEMA_EXPIRY_BANDS` in `types/femaTenant.types.ts`.
 */
export const VALID_FEMA_EXPIRY_BANDS = FEMA_EXPIRY_BANDS;
export type { FemaExpiryBand };

/**
 * Multi-COG contract §4: the ONE tenant-level credential state (8 values). The one list lives in
 * `types/femaTenant.types.ts` (`FEMA_CREDENTIAL_STATES`). These names are aliases of that list.
 */
export const VALID_FEMA_META_CREDENTIAL_STATES = FEMA_CREDENTIAL_STATES;
export type FemaMetaCredentialState = FemaCredentialState;

/** Multi-COG contract §2 (`ZFemaMetaReasonCode`): why a held class cannot send. */
export const VALID_FEMA_META_REASON_CODES = [
  'SERVICE_NOT_CONFIGURED',
  'SERVICE_UNREACHABLE',
  'TENANT_DISABLED',
  'CREDENTIAL_NOT_LOADED',
  'CREDENTIAL_NOT_VALIDATED',
  'CERTIFICATE_EXPIRED',
  'CLASS_NOT_SERVED',
] as const;
export type FemaMetaReasonCode = (typeof VALID_FEMA_META_REASON_CODES)[number];

/** RescAlert: the display label for the destination — FEMA's IPAWS-OPEN aggregator. */
export type FemaDestination = 'IPAWS-OPEN';

/**
 * RescAlert: `GET /fusion/v2/fema/meta` — what the Alert Creator's banner shows before an
 * operator composes (card FE-01: the environment is read from the server, never a build-time
 * constant). The edge relays the IpawsService's status snapshot, mapped by
 * Backend `src/features/fema/ops/FEMAMeta.ops.ts`; this mirrors that `FemaMeta`.
 */
export type FemaMetaTenant = {
  cogId: string;
  cogName?: string | null;
  organizationName: string;
  credentialLoaded: boolean;
  certificateNotAfter: string | null;
  daysToExpiry: number | null;
};

/**
 * Multi-COG contract §2 (`ZFemaMetaEnvironment`): one class that the session workplace holds.
 * `canSend` = `served && enabled && credentialState in ('active', 'expiring')`. The backend
 * computes it. The front end does not compute it again. `reason` is one operator sentence.
 */
export type FemaMetaEnvironment = {
  environment: FemaEnvironmentClass;
  cogId: string;
  cogName: string | null;
  organizationName: string | null;
  isProduction: boolean;
  served: boolean;
  enabled: boolean;
  credentialState: FemaMetaCredentialState;
  credentialLoaded: boolean;
  certificateNotAfter: string | null;
  daysToExpiry: number | null;
  expiryBand: FemaExpiryBand | null;
  canSend: boolean;
  reasonCode: FemaMetaReasonCode | null;
  reason: string | null;
};

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

/**
 * Type guard for one `environments[]` entry of `GET /fusion/v2/fema/meta`. The front end has no
 * Zod. This guard checks every field of the contract shape.
 */
export const isFemaMetaEnvironment = (value: unknown): value is FemaMetaEnvironment => {
  if (!value || typeof value !== 'object') return false;
  const entry: Readonly<Record<string, unknown>> = { ...value };
  return (
    isFemaEnvironmentClass(entry.environment) &&
    typeof entry.cogId === 'string' &&
    isNullableString(entry.cogName) &&
    isNullableString(entry.organizationName) &&
    typeof entry.isProduction === 'boolean' &&
    typeof entry.served === 'boolean' &&
    typeof entry.enabled === 'boolean' &&
    VALID_FEMA_META_CREDENTIAL_STATES.some((state) => state === entry.credentialState) &&
    typeof entry.credentialLoaded === 'boolean' &&
    isNullableString(entry.certificateNotAfter) &&
    (entry.daysToExpiry === null || Number.isInteger(entry.daysToExpiry)) &&
    (entry.expiryBand === null ||
      VALID_FEMA_EXPIRY_BANDS.some((band) => band === entry.expiryBand)) &&
    typeof entry.canSend === 'boolean' &&
    (entry.reasonCode === null ||
      VALID_FEMA_META_REASON_CODES.some((code) => code === entry.reasonCode)) &&
    isNullableString(entry.reason)
  );
};

export type FemaMeta = {
  destination: FemaDestination;
  /** `TDL` | `DEMO` | `PRODUCTION` as the service runs; any other value is shown as it came. */
  environment: string;
  endpoint: { url: string | null; lastReachableAt: string | null };
  tenants: FemaMetaTenant[];
  service: { name: string; version: string; startedAt: string };
  probes: { refreshedAt: string | null; databaseReachable: boolean; lastError: string | null };
  checkedAt: string;
  /**
   * Multi-COG contract §2: the first class that can send, never `production`. `null` when no
   * held class can be a default. An older backend does not send it.
   */
  defaultEnvironment?: FemaEnvironmentClass | null;
  /**
   * Multi-COG contract §2: ONLY the classes that the session workplace holds, in the order tdl,
   * demo, production. ABSENT = an older backend: the front end keeps the single-class behaviour
   * and sends no class.
   */
  environments?: FemaMetaEnvironment[];
};

/** JSON Schema document served by `GET /fusion/v2/fema/meta/type`. */
export type FemaJsonSchema = Record<string, unknown>;

/** The backend-owned submission contract used to keep controlled form values aligned. */
export type FemaAlertContract = {
  name: 'FemaAlertSubmission';
  schema: FemaJsonSchema;
  forbidden: {
    alert: string[];
    info: string[];
  };
  /**
   * Backend (card BE-12): the static channel facts from IDG v4.06. The form reads it through
   * `readIpawsChannelRules` (AlertCreator `utils/ipawsChannelRules.utils.ts`). That function checks
   * each value, because an older server does not send the block.
   */
  dissemination?: FemaDisseminationContract;
};

/**
 * IDG v4.06 §8.4 p64: FEMA grants event codes and geocodes per dissemination channel. The COG
 * profile keys its grants by these five tokens (Backend `IS/types/cogProfile.types.ts:51`).
 */
export const FEMA_DISSEMINATION_CHANNELS = ['NWEM', 'EAS', 'CMAS', 'CAPEXCH', 'PUBLIC'] as const;
export type FemaDisseminationChannel = (typeof FEMA_DISSEMINATION_CHANNELS)[number];

/** IDG v4.06 Table 16 p29: the SAME organization codes a COG profile can hold (decision D18). */
export const FEMA_EAS_ORG_CODES = ['PEP', 'EAS', 'WXR', 'CIV'] as const;
export type FemaEasOrgCode = (typeof FEMA_EAS_ORG_CODES)[number];

/** Event codes per channel. A missing channel key means "no code on this channel". */
export type FemaEventCodesByChannel = Partial<Record<FemaDisseminationChannel, string[]>>;

/** The three channels a `BLOCKCHANNEL` value can name (IDG §8.5 p64-65). */
export type FemaBlockableChannel = 'NWEM' | 'EAS' | 'CMAS';

/**
 * Backend (card BE-12): `GET /fusion/v2/fema/meta/type` → `dissemination.limits`. The names and
 * the shape mirror Backend `ZDisseminationContract`
 * (`src/features/fema/types/disseminationChannels.types.ts`).
 */
export type FemaDisseminationLimits = {
  /** IDG Table 76 p73, FEMA 506: EAS `<expires>` ≤ send time + this many hours. */
  easExpiresHours: number;
  /** IDG §8.11 p92, FEMA 406: NWEM `<expires>` ≤ send time + this many hours. */
  nwemExpiresHours: number;
  /** IDG Table 76 p73, FEMA 613: CMAS `<expires>` ≤ send time + this many hours. */
  cmasExpiresHours: number;
  cmasMaxShapes: number;
  cmasMaxCoordinatePairs: number;
  easMaxGeocodes: number;
  nwemMaxGeocodes: number;
  easMaxTextCharacters: number;
  nwrMaxPolygonVertices: number;
  nwrCoordinateDecimals: number;
  cmasCoordinateDecimals: number;
  cmasUrgency: string[];
  cmasSeverity: string[];
  cmasCertainty: string[];
  cmasResponseTypes: string[];
  cmamProhibitedCharacters: string[];
};

/** IDG Table 86 p91: one cell of the WEA handling matrix. */
export type FemaWeaHandlingAllowance = 'Required' | 'Recommended' | 'Optional' | 'Not Allowed';

/** IDG Table 86 p91: one row of the WEA handling matrix, an event code and its cells. */
export type FemaWeaHandlingMatrixRow = {
  eventCode: string;
  description: string;
  /** The notes the table attaches to the row, such as `Note-1`. */
  notes: string[];
  /** Keyed by the WEA handling value, such as `Public Safety`. */
  handling: Record<string, FemaWeaHandlingAllowance>;
};

/** Backend (card BE-12): `GET /fusion/v2/fema/meta/type` → `dissemination`. */
export type FemaDisseminationContract = {
  channels: FemaBlockableChannel[];
  blockParameter: 'BLOCKCHANNEL';
  /** IDG Tables 79/80: the codes each channel can carry at all. This is not the COG grant. */
  eventCodesByChannel: Record<FemaBlockableChannel, string[]>;
  /** IDG Table 80: the codes only FEMA may send. */
  femaOnlyEventCodes: string[];
  weaHandling: {
    values: string[];
    matrix: FemaWeaHandlingMatrixRow[];
    /** The notes under IDG Table 86 p91, keyed `Note-1`, `Note-2`, `Note-3`. */
    notes: Record<string, string>;
  };
  easOrgCodes: FemaEasOrgCode[];
  limits: FemaDisseminationLimits;
};

/**
 * RescAlert: one area the workplace's COG may alert — `GET /fusion/v2/fema/jurisdictions`. One
 * per SAME code in the COG Profile FEMA issues (card BE-13: the profile is the authority, per
 * dissemination channel), with the county's Census ring so the map can draw it. Mirrors Backend
 * `src/features/fema/ops/FEMAJurisdictions.ops.ts`.
 */
export type FemaJurisdiction = {
  /** PSSCCC — P a county part (0 = whole), SS state FIPS, CCC county FIPS. */
  sameCode: string;
  /** SSCCC — the Census county GEOID. */
  geoid: string;
  part: string;
  wholeState: boolean;
  name: string;
  state: string | null;
  /** The dissemination channels FEMA authorized this code on. */
  channels: string[];
  polyType: 'Polygon' | 'MultiPolygon' | null;
  /**
   * The Census ring as stored, JSON-serialised — the county's, or the state's once the backend's
   * `census:states` table is loaded; null when no ring is known.
   */
  boundary_coordinates: string | null;
};

export type FemaJurisdictions = {
  cogId: string | null;
  environment: string | null;
  organizationName: string | null;
  fetchedAt: string | null;
  tenantSource: 'workplace' | 'service-default';
  channels: string[];
  /**
   * WEA Handling values allowed by the active COG. The service may further constrain a value to
   * specific message fields; absent metadata means the frontend must not invent live options.
   */
  weaHandlingOptions?: FemaWeaHandlingOption[];
  /**
   * Backend (card BE-12): the COG profile's event-code grant per channel (IDG Table 16 p29). An
   * older server does not send it.
   */
  eventCodesByChannel?: FemaEventCodesByChannel;
  /**
   * Backend (card BE-12, decision D18): the COG profile `organizationName` when it is one of the
   * four SAME organization codes, else `null`. Never the COG name and never the `/meta` tenant
   * name (design note finding 3).
   */
  easOrgCode?: FemaEasOrgCode | null;
  /** The union of the per-channel event-code grants. */
  authorizedEventCodes?: string[];
  jurisdictions: FemaJurisdiction[];
};

export type FemaWeaHandlingOption = {
  /** Exact FEMA value written to the WEAHandling CAP parameter. */
  value: string;
  /** Approved operator-facing name and explanation supplied with the COG profile. */
  label: string;
  description?: string;
  eventCodes?: string[];
  statuses?: FemaAlertStatus[];
  environments?: string[];
  urgencies?: FemaAlertInfo['urgency'][];
  severities?: FemaAlertInfo['severity'][];
  certainties?: FemaAlertInfo['certainty'][];
};

/**
 * CAP §3.2.2 / §3.2.4: eventCode, parameter and geocode share one shape — a
 * user-assigned `valueName` naming the domain and a string `value`. "Values
 * of valueName that are acronyms SHOULD be represented in all capital
 * letters without periods (e.g., SAME, FIPS, ZIP)."
 */
export type FemaValueCode = {
  valueName: string;
  value: string;
};

/**
 * CAP §3.3.1 (WGS-84) + §1.5: a coordinate pair is `latitude,longitude` —
 * the OPPOSITE of GeoJSON. Named fields so a transposition cannot be silent.
 * Backend refines the ranges: latitude -90..90, longitude -180..180.
 */
export type FemaCoordinate = {
  latitude: number;
  longitude: number;
};

/**
 * A circle is sent as one center coordinate and a radius in kilometers.
 * CAP §3.2.4 circle: "The circular area is represented by a central point
 * given as a [WGS 84] coordinate pair followed by a space character and a
 * radius value in kilometers." CAP does not state a positive minimum; the
 * Alert Creator requires a value above zero so every drawn circle covers a real area.
 */
export type FemaCircle = {
  center: FemaCoordinate;
  radius: number;
};

/** CAP §3.2.3 <resource> — one per referenced file. */
export type FemaAlertResource = {
  /** REQUIRED (CAP §3.2.3): human-readable type/content, e.g. "map", "photo". */
  resourceDesc: string;
  /**
   * REQUIRED (CAP §3.2.3): MIME type per RFC 2046 — ANY registered type (the
   * standard's own A.1 example uses image/gif). Profile Table 1: the four
   * `x-ipaws-*` types "SHALL be used to identify broadcast content for
   * delivery to the public" — that is EAS BROADCAST content only, paired with
   * resourceDesc "EAS Broadcast Content" (case sensitive).
   */
  mimeType: string;
  /** CAP §3.2.3: approximate size in bytes; zero is legal. SHOULD accompany <uri>. */
  size?: number;
  /** CAP §3.2.3: full absolute URI — or a relative URI naming the derefUri content. */
  uri?: string;
  /**
   * CONDITIONAL (CAP §3.2.3): base-64 content for one-way links. "MUST NOT be
   * used unless the sender is certain that all direct clients are capable of
   * processing it." Wire form: the base64 STRING; Backend BuildFemaAlert
   * decodes it to the model's Buffer (BE-04).
   */
  derefUri?: string;
  /**
   * CAP §3.2.3: SHA-1 of the resource file per FIPS 180-2 — exactly 20 bytes
   * once decoded. Wire form: base64 string (Backend decodes, BE-04).
   */
  digest?: string;
};

/** CAP §3.2.2 category — REQUIRED, repeatable. The twelve values of the dictionary. */
export type FemaCategory =
  | 'Geo'
  | 'Met'
  | 'Safety'
  | 'Security'
  | 'Rescue'
  | 'Fire'
  | 'Health'
  | 'Env'
  | 'Transport'
  | 'Infra'
  | 'CBRNE'
  | 'Other';

/** CAP §3.2.2 urgency — REQUIRED. urgency/severity/certainty "distinguish emphasis" together. */
export type FemaUrgency = 'Immediate' | 'Expected' | 'Future' | 'Past' | 'Unknown';
/** CAP §3.2.2 severity — REQUIRED. */
export type FemaSeverity = 'Extreme' | 'Severe' | 'Moderate' | 'Minor' | 'Unknown';
/**
 * CAP §3.2.2 certainty — REQUIRED. The deprecated CAP 1.0 value "Very Likely"
 * SHOULD be treated as `Likely` — the Backend normalizes it; the form never
 * offers it.
 */
export type FemaCertainty = 'Observed' | 'Likely' | 'Possible' | 'Unlikely' | 'Unknown';

/**
 * CAP §3.2.2 responseType — optional, repeatable. `Assess` "SHOULD NOT be
 * used in public warning applications" — the form filters it out
 * (publicWarningResponseTypeOptions in Ipaws.config.ts).
 */
export type FemaResponseType =
  | 'Shelter'
  | 'Evacuate'
  | 'Prepare'
  | 'Execute'
  | 'Avoid'
  | 'Monitor'
  | 'Assess'
  | 'AllClear'
  | 'None';

/**
 * CAP §3.2.4 <area>. Profile Table 1: "At least one <area> block MUST be
 * present" in what we send (base CAP leaves it optional). The area is the
 * UNION of all included shapes.
 */
export type FemaAlertArea = {
  /** REQUIRED (CAP §3.2.4): "A text description of the affected area." */
  areaDesc: string;
  /**
   * CAP §3.2.4 polygon: "A minimum of 4 coordinate pairs MUST be present and
   * the first and last pairs of coordinates MUST be the same." The form's
   * three drawn vertices become four with the closing point appended
   * (ipawsSubmission.utils.ts); the Turf checks in ipawsPolygon.utils.js are
   * RescAlert's stricter pre-flight (self-intersection, collinear rings).
   */
  polygon?: FemaCoordinate[][];
  circle?: FemaCircle[];
  /**
   * CAP §3.2.4 geocode: "This element is primarily for compatibility with
   * other systems. … it SHOULD be used in concert with an equivalent
   * description in the more universally understood <polygon> and <circle>
   * forms whenever possible." Profile Table 1: "At least one instance of
   * <geocode> with a <valueName> of "SAME" and a value of a SAME 6-digit
   * location (extended FIPS) SHOULD be used." and "If a SAME-based <geocode>
   * is not present, IPAWS exchange partners unable to use a geospatial
   * representation may ignore the message."
   */
  geocode?: FemaValueCode[];
  /**
   * CAP §3.2.4: the measure "is in feet above mean sea level per the
   * [WGS 84] datum". Zero is sea level and legal.
   */
  altitude?: number;
  /** CAP §3.2.4: ceiling "MUST NOT be used except in combination with the <altitude> element." */
  ceiling?: number;
};

/**
 * CAP §3.2.2 <info>. Profile Table 1 (info): all <info> blocks "MUST relate
 * to a single incident or update, with the same <category> and <eventCode>
 * values" — multiple blocks carry LANGUAGES, not extra incidents (the form's
 * extra-language blocks copy category/eventCode from the primary).
 */
export type FemaAlertInfo = {
  /** REQUIRED, repeatable (CAP §3.2.2). RescAlert: no default — an empty list is a finding. */
  category: FemaCategory[];
  /** REQUIRED (CAP §3.2.2): "text denoting the type of the subject event". */
  event: string;
  /** Optional, repeatable (CAP §3.2.2). RescAlert: omitted when the operator chose none — never `None` by default. */
  responseType?: FemaResponseType[];
  /** REQUIRED (CAP §3.2.2). */
  urgency: FemaUrgency;
  /** REQUIRED (CAP §3.2.2). */
  severity: FemaSeverity;
  /** REQUIRED (CAP §3.2.2). */
  certainty: FemaCertainty;
  /** CAP §3.2.2: RFC 3066 code; absent (or null) "SHALL be assumed" `en-US`. */
  language?: string;
  /** CAP §3.2.2: intended audience, free text. */
  audience?: string;
  /**
   * CAP §3.2.2: repeatable system-specific event typing, e.g. SAME/FLW —
   * OPTIONAL in base CAP. Profile Table 1: REQUIRED; "Messages intended for
   * EAS, CMAS and HazCollect dissemination MUST include one and only one
   * instance of this with a <valueName> of "SAME" and using a SAME-standard
   * three-letter value." The three-letter values are FCC §11.31(e); FEMA's
   * glossary describes their use with IPAWS; the form's list is the subset in
   * Ipaws.config.ts (see its source note); the tenant's FEMA-authorized
   * subset is the future `/meta` answer (BE-08).
   */
  eventCode: FemaValueCode[];
  /**
   * CAP §3.2.2: if absent, "the effective time SHALL be assumed to be the
   * same as in <sent>". Profile: "Ignored if present. Alerts SHALL be
   * effective upon issuance." Legal to send; consumers ignore it.
   */
  effective?: string | Date;
  /** CAP §3.2.2 onset. Profile: ignored if present (see effective). */
  onset?: string | Date;
  /**
   * CAP §3.2.2: if absent, "each recipient is free to set its own policy" —
   * OPTIONAL in base CAP. Profile Table 1: REQUIRED. Wire form: an ISO string
   * in CAP §3.3.2 dateTime form `YYYY-MM-DDThh:mm:ss±hh:mm`; "Alphabetic
   * timezone designators such as 'Z' MUST NOT be used. The timezone for UTC
   * MUST be represented as '-00:00'." (formatIpawsDateTimeWithOffset applies
   * that; the Backend serializer re-renders it anyway.)
   */
  expires: string | Date;
  /**
   * CAP §3.2.2 senderName — human-readable originator. Backend (BE-03):
   * STAMPED on every <info> from the tenant row in FEMA's `cogId,org,org`
   * form; a client-supplied value is refused. The form never sets it.
   */
  senderName?: string;
  /**
   * CAP §3.2.2: "160 characters MAY be a useful target limit for headline
   * length" — a recommendation. RescAlert: a HARD cap of 160 (Mike,
   * 2026-08-21; changing it needs Mike's and Wesley's sign-off; enforced by
   * ZCapAlertRescAlert in the backend).
   */
  headline?: string;
  /** CAP §3.2.2: extended human-readable description. RescAlert form: required. */
  description?: string;
  /**
   * CAP §3.2.2 (0..1) — at most ONE per <info>. "If different instructions
   * are intended for different recipients, they should be represented by use
   * of multiple <info> blocks".
   */
  instruction?: string;
  /** CAP §3.2.2: full absolute URI for additional reference information. */
  web?: string;
  /** CAP §3.2.2: contact for follow-up and confirmation. */
  contact?: string;
  /**
   * CAP §3.2.2: repeatable system-specific parameters. Where the channel
   * parameters live: Profile Table 2 — `EAS-ORG` (EAS/HazCollect MUST
   * include it), `CMAMtext` ("free form text limited in length to 90 English
   * characters"), `EAS-Must-Carry`; FEMA/FCC (not OASIS) — `CMAMlongtext`
   * ≤ 360 characters, `WEAHandling`, `BLOCKCHANNEL`. The 90 and the 360 are
   * INDEPENDENTLY AUTHORED, never derived from each other (FEMA digest §5.2).
   */
  parameter?: FemaValueCode[];
  /** CAP §3.2.3 <resource>, repeatable. */
  resource?: FemaAlertResource[];
  /** CAP §3.2.4 <area>, repeatable. Profile Table 1: at least one MUST be present. */
  area?: FemaAlertArea[];
};

/**
 * CAP §3.2.1 msgType — REQUIRED. Update/Cancel/Ack/Error operate on
 * <references>. Profile: "All related messages that have not yet expired
 * MUST be referenced for "Update" and "Cancel" messages." (needs the message
 * store — card BE-11).
 */
export type FemaMessageType = 'Alert' | 'Update' | 'Cancel' | 'Ack' | 'Error';

/**
 * CAP §3.2.1 status — REQUIRED. `Exercise`: the exercise identifier SHOULD
 * appear in <note>. Profile: "A value of "Actual" SHALL be used for messages
 * intended for dissemination to the public, including test messages intended
 * for delivery to the public." RescAlert: the form offers `Test` only until
 * public dissemination is explicitly approved (statusOptions in
 * Ipaws.config.ts; 2026-08-24 review, item 6).
 */
export type FemaAlertStatus = 'Actual' | 'Exercise' | 'System' | 'Test' | 'Draft';

/**
 * CAP §3.2.1 scope — REQUIRED. Public: addresses optional. Restricted:
 * <restriction> describes the limiting rule. Private: addresses "Required
 * when <scope> is 'Private'".
 */
export type FemaAlertScope = 'Public' | 'Private' | 'Restricted';

/**
 * CAP §3.2.1 references: the extended message identifier of an earlier
 * message "in the form sender,identifier,sent". Structural here; the
 * Backend serializer renders the `sender,identifier,sent` triplets.
 */
export type FemaReference = {
  sender: string;
  identifier: string;
  sent: string | Date;
};

/**
 * The wire payload the frontend sends to `POST /v2/fema` as CAP alert CONTENT
 * (CAP §3.2.1 <alert>). The ENVELOPE is the backend's, and a payload that
 * carries any of it is REFUSED with a finding, never overwritten:
 *   sent       — stamped at origination (Backend BuildFemaAlert, BE-04)
 *   source     — the constant `180303 v1.2.1`, ≤ 30 chars, "system generated,
 *                never client-derived" (FEMA §4.5; card BE-06)
 *   sender     — the tenant's registered originator (BE-03)
 *   senderName — the tenant's, on every <info> (BE-03)
 *   code       — 'IPAWSv1.0' is ENSURED by the backend (Profile Table 1);
 *                sibling codes the form passes are kept
 */
export type FemaAlertPayload = {
  /** REQUIRED (CAP §3.2.1) — see FemaAlertStatus. */
  status: FemaAlertStatus;
  /** REQUIRED (CAP §3.2.1) — see FemaMessageType. */
  msgType: FemaMessageType;
  /**
   * CAP §3.2.1: optional, repeatable, user-defined handling codes. Profile
   * Table 1: "Value SHALL include the string "IPAWSv1.0" to indicate the
   * Profile version in use." — the Backend ensures that marker; only extra
   * codes belong here.
   */
  code?: string[];
  /** CAP §3.2.1: "The message note is primarily intended for use with <status> "Exercise" and <msgType> "Error"." */
  note?: string;
  /** CAP §3.2.1: earlier messages this one refers to (Update/Cancel/Ack/Error). */
  references?: FemaReference[];
  /** CAP §3.2.1: incident names; the serializer quotes ones containing whitespace. */
  incidents?: string[];
  /** REQUIRED (CAP §3.2.1) — see FemaAlertScope. */
  scope: FemaAlertScope;
  /** CAP §3.2.1: the rule limiting distribution — used with scope Restricted. */
  restriction?: string;
  /**
   * CAP §3.2.1: "Multiple space-delimited addresses MAY be included.
   * Addresses including whitespace MUST be enclosed in double-quotes." —
   * an ARRAY on the wire (the Backend serializer encodes the quoting);
   * REQUIRED when scope is Private. The form's free-form field is split by
   * parseCapAddresses; the key is absent when the operator gave none.
   */
  addresses?: string[];
  /** CAP §3.2.2, repeatable. Profile: at least one <info> block. */
  info: FemaAlertInfo[];
};

export type FemaSubmitPayload = FemaAlertPayload;

/**
 * RescAlert (Mike, 2026-08-24): the FEMA SEND GATE. While the `sendToTdl`
 * line in Backend postMessage.controller.ts is commented out, a good message
 * is built, serialized, signed and verified — and NOT sent — and this is the
 * 200 body. Never present it as sent; it is not a FEMA outcome class.
 */
export type FemaSendGateResponse = {
  code: 'REACHED_SEND_POINT';
  message: string;
  /** The resolved tenant: FEMA's COG id (120188 = RescAlert's TDL identity) + environment class. */
  tenant: { cogId: string; environment: string };
  /** The CAP <identifier> of the signed document. */
  capIdentifier: string;
  /** XML-DSig DigestValue (SHA-256 over exc-c14n; FEMA §4.6 signature requirements). */
  digestValue: string;
  /** The signing certificate's subject (the dev-COG stand-in until the real COG copy is converted). */
  subjectName: string;
  /** The exact signed bytes — never reformat (whitespace is inside the signature). */
  signedXml: string;
  /** The same document indented for reading. Display only — Backend FormatXmlForDisplay. */
  signedXmlFormatted: string;
};

/**
 * FEMA §4.8: the SEVEN result classes of a submission, as the backend names
 * them (mirrors IpawsResultClass in Backend ExtServices/IpawsService/src/types/ipaws.types.ts).
 * FEMA states twice that HTTP 200 is NOT IPAWS acceptance — transport and
 * authentication success are steps, not results.
 */
export type FemaBackendResultClass =
  | 'TRANSPORT_SUCCESS'
  | 'AUTHENTICATION_SUCCESS'
  | 'IPAWS_ACCEPTANCE'
  | 'IPAWS_ADVISORY'
  | 'IPAWS_WARNING'
  | 'IPAWS_REJECTION'
  | 'NETWORK_FAILURE';

/** Backend: the send result once transport exists (mirrors IpawsSendResult, BE-07). */
export type FemaBackendSendResult = {
  resultClass: FemaBackendResultClass;
  capIdentifier: string | null;
  /** FEMA's advisory/warning/error entries — e.g. code 225 is an advisory under release 4.06 and an ERROR under 4.08 (FEMA §4.5). */
  advisories: { code: string; message: string }[];
  rawResponse: string | null;
  sentAt: string | null;
  /** The resolved tenant the message was sent under — present so the FE can link to that COG's Message Viewer. */
  tenant?: { cogId: string; environment: string };
};

/**
 * RescAlert: the response CHANNEL a FEMA status item came from (FEMA's "Status Item Response
 * Detail" table). `UNKNOWN` covers a code outside the registry, so a lookup never throws.
 */
export const FEMA_STATUS_CHANNELS = [
  'MOBILE_CARRIER',
  'CAP_EXCHANGE',
  'IPAWS_PROFILE',
  'NWEM',
  'EAS',
  'CMAS',
  'PUBLIC_FEED',
  'UNKNOWN',
] as const;
export type FemaStatusChannel = (typeof FEMA_STATUS_CHANNELS)[number];

/**
 * RescAlert: one row of the FEMA status/advisory code registry (services/ipaws/config/femaStatusCodes.ts).
 * `disseminated` is set only on a channel's own Ack (`true`) or "not disseminated" status (`false`);
 * every other code is a validation/authorization detail and leaves it undefined.
 */
export type FemaStatusCodeInfo = {
  code: string;
  channel: FemaStatusChannel;
  isError: boolean;
  label: string;
  remark: string;
  disseminated?: boolean;
  /** True for a non-error code that confirms the message itself is valid (e.g. 200 schema, 202 signature). */
  validationSuccess?: boolean;
};

/** A backend advisory entry enriched with the registry's channel/error/remark for that code. */
export type FemaEnrichedAdvisory = FemaStatusCodeInfo & {
  /** The backend's own text for this code. */
  backendMessage: string;
};

/** Everything `POST /v2/fema` can answer with a 2xx. */
export type FemaSubmitResponse =
  | FemaSubmissionOutcome
  | FemaSendGateResponse
  | FemaBackendSendResult;

/**
 * RescAlert (Sebastian, `features/ipaws/submission-and-response`): the SIX
 * outcome classes the result panel shows. The backend's seven (FEMA §4.8)
 * fold onto them in ipaws.service.ts mapBackendResultClass — transport and
 * authentication success both render as `transport-unknown` ("not
 * confirmed"), because HTTP 200 is not acceptance.
 */
export const FEMA_SUBMISSION_OUTCOME_CLASSES = [
  'acceptance',
  'advisory',
  'warning',
  'accepted-not-disseminated',
  'rejection',
  'transport-unknown',
  'network-failure',
] as const;

export type FemaSubmissionOutcomeClass = (typeof FEMA_SUBMISSION_OUTCOME_CLASSES)[number];

/**
 * Backend (BE-05): one finding per field, `field` = the dotted payload path
 * (`info.0.headline`), `message` in the spec's words, `code` reserved for the
 * stable finding codes (BE-05 tail). The backend also sends `stage`
 * (shape | schema | serialize | geography), which the panel does not need.
 * `geography` (2026-08-28): a shape outside the authorized area the alert
 * targets, or a code the COG does not hold — the backend RECORDS such a
 * refusal (`Ipaws_Message.state` = `rejected-geography`) before answering,
 * and the 400 carries `recorded: { messageId, state } | null` beside the findings.
 */
export type FemaOutcomeFieldFinding = {
  field: string;
  message: string;
  code?: string;
  stage?: 'shape' | 'schema' | 'serialize' | 'geography';
};

export type FemaSubmissionOutcomeBase = {
  outcome: FemaSubmissionOutcomeClass;
  /**
   * The class that the backend answered under (`body.tenant.environment`, upper case). `null`
   * when the answer names no known class. Never a front-end constant (multi-COG contract §1).
   */
  environment: FemaEnvironment | null;
  destination: FemaDestination;
  /** RescAlert: when the frontend received the answer (ISO). */
  receivedAt: string;
  rawStatusCode?: number;
};

/** FEMA §4.8 IPAWS acceptance: the explicit acceptance response carrying the CAP identifier. */
export type FemaSubmissionAcceptanceOutcome = FemaSubmissionOutcomeBase & {
  outcome: 'acceptance';
  capIdentifier: string;
  acceptedAt: string;
  /** FEMA's Message Viewer for the environment (TDL: messageviewer.tdl.apps.fema.gov) — the G1 proof (BE-09). */
  verificationUrl?: string;
  /** The COG the message was sent under, so the viewer link can say which COG to look at. */
  cogId?: string;
};

/** FEMA §4.8 advisory: accepted and disseminated, with a condition to fix (e.g. 225 under 4.06). */
export type FemaSubmissionAdvisoryOutcome = FemaSubmissionOutcomeBase & {
  outcome: 'advisory';
  capIdentifier: string;
  advisoryCode: string;
  advisoryMessage: string;
  /** Every status item FEMA returned, enriched with the registry — not just the first. */
  advisories: FemaEnrichedAdvisory[];
  verificationUrl?: string;
  cogId?: string;
};

/** FEMA §4.8 warning: accepted with a dissemination concern. */
export type FemaSubmissionWarningOutcome = FemaSubmissionOutcomeBase & {
  outcome: 'warning';
  capIdentifier: string;
  warningCode?: string;
  warningMessage: string;
  /** Every status item FEMA returned, enriched with the registry — not just the first. */
  advisories: FemaEnrichedAdvisory[];
  verificationUrl?: string;
  cogId?: string;
};

/**
 * RescAlert: accepted by IPAWS but the channel's own status says it was NOT pushed out (e.g. 301
 * non-IPAWS-alert, 401/501/601/801 message-not-disseminated-as-*). Distinct from `advisory` so the
 * UI never reads this as a plain success — the alert did not reach the public.
 */
export type FemaSubmissionNotDisseminatedOutcome = FemaSubmissionOutcomeBase & {
  outcome: 'accepted-not-disseminated';
  capIdentifier: string;
  reason: string;
  advisories: FemaEnrichedAdvisory[];
  verificationUrl?: string;
  cogId?: string;
};

/** FEMA §4.8 rejection: not disseminated; the reason and, when field-level, the findings. */
export type FemaSubmissionRejectionOutcome = FemaSubmissionOutcomeBase & {
  outcome: 'rejection';
  rejectionCode?: string;
  rejectionReason: string;
  findings?: FemaOutcomeFieldFinding[];
  /** Every status item FEMA returned, enriched with the registry — not just the rejecting one. */
  advisories?: FemaEnrichedAdvisory[];
};

/** FEMA §4.8 transport/authentication success without an IPAWS acceptance — "not confirmed". */
export type FemaSubmissionTransportUnknownOutcome = FemaSubmissionOutcomeBase & {
  outcome: 'transport-unknown';
  transportMessage: string;
  correlationId?: string;
};

/** FEMA §4.8 network failure: nothing reached IPAWS; retry is the operator's call. */
export type FemaSubmissionNetworkFailureOutcome = FemaSubmissionOutcomeBase & {
  outcome: 'network-failure';
  errorMessage: string;
  retryable: true;
};

export type FemaSubmissionOutcome =
  | FemaSubmissionAcceptanceOutcome
  | FemaSubmissionAdvisoryOutcome
  | FemaSubmissionWarningOutcome
  | FemaSubmissionNotDisseminatedOutcome
  | FemaSubmissionRejectionOutcome
  | FemaSubmissionTransportUnknownOutcome
  | FemaSubmissionNetworkFailureOutcome;
